import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const SVG_HEIGHT = 350;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '240px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
  legendContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '16px',
  },
  legendBar: {
    width: '200px',
    height: '10px',
    borderRadius: '5px',
  },
  legendLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '200px',
    marginTop: '4px',
    fontSize: '10px',
    color: '#6b7280',
  },
};

/**
 * Determine whether white text is readable on a given background color.
 * Uses relative luminance per WCAG contrast guidelines.
 */
function shouldUseWhiteText(hexColor) {
  const r = parseInt(hexColor.slice(1, 3), 16) / 255;
  const g = parseInt(hexColor.slice(3, 5), 16) / 255;
  const b = parseInt(hexColor.slice(5, 7), 16) / 255;

  const toLinear = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

  return luminance < 0.45;
}

export default function CoverageTreemap({ data }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;
    if (!containerRef.current) return;

    const containerWidth = containerRef.current.getBoundingClientRect().width;
    if (containerWidth <= 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', containerWidth).attr('height', SVG_HEIGHT);

    // Color scale: coverage 0% (red) -> 25% (orange) -> 50% (yellow) -> 75% (light green) -> 100% (dark green)
    const coverageColorScale = d3.scaleLinear()
      .domain([0, 25, 50, 75, 100])
      .range(['#dc2626', '#f97316', '#fbbf24', '#4ade80', '#16a34a'])
      .interpolate(d3.interpolateRgb)
      .clamp(true);

    // Build hierarchy
    const root = d3.hierarchy({ children: data })
      .sum((d) => d.addressCount || 0)
      .sort((a, b) => b.value - a.value);

    const treemap = d3.treemap()
      .size([containerWidth, SVG_HEIGHT])
      .padding(2)
      .tile(d3.treemapSquarify);

    treemap(root);

    // Create clip paths for rounded rectangles
    const defs = svg.append('defs');
    root.leaves().forEach((leaf, i) => {
      const w = leaf.x1 - leaf.x0;
      const h = leaf.y1 - leaf.y0;
      defs.append('clipPath')
        .attr('id', `treemap-clip-${i}`)
        .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', w)
        .attr('height', h)
        .attr('rx', 3)
        .attr('ry', 3);
    });

    // Draw rectangles
    const cells = svg.selectAll('.cell')
      .data(root.leaves())
      .enter()
      .append('g')
      .attr('class', 'cell')
      .attr('transform', (d) => `translate(${d.x0}, ${d.y0})`);

    cells.each(function (d, i) {
      const g = d3.select(this);
      const w = d.x1 - d.x0;
      const h = d.y1 - d.y0;
      const fillColor = coverageColorScale(d.data.mtcCoverage);
      const hexColor = d3.color(fillColor).formatHex();
      const useWhite = shouldUseWhiteText(hexColor);
      const textColor = useWhite ? '#ffffff' : '#1f2937';

      // Background rectangle
      g.append('rect')
        .attr('width', w)
        .attr('height', h)
        .attr('fill', fillColor)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1)
        .attr('rx', 3)
        .attr('ry', 3)
        .style('cursor', 'pointer');

      // Labels (only if rectangle is large enough)
      if (w > 50 && h > 40) {
        const clipId = `treemap-clip-${i}`;
        const labelGroup = g.append('g')
          .attr('clip-path', `url(#${clipId})`);

        // State abbreviation
        labelGroup.append('text')
          .attr('x', w / 2)
          .attr('y', h / 2 - 8)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .attr('font-size', '14px')
          .attr('font-weight', '700')
          .attr('fill', textColor)
          .text(d.data.state);

        // Address count
        labelGroup.append('text')
          .attr('x', w / 2)
          .attr('y', h / 2 + 6)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .attr('font-size', '10px')
          .attr('fill', textColor)
          .attr('opacity', 0.9)
          .text(`${d.data.addressCount} addr`);

        // MTC coverage percentage
        labelGroup.append('text')
          .attr('x', w / 2)
          .attr('y', h / 2 + 18)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .attr('font-size', '10px')
          .attr('fill', textColor)
          .attr('opacity', 0.9)
          .text(`${Math.round(d.data.mtcCoverage)}% MTC`);
      }
    });

    // Hover interactions (attach to each cell group)
    cells
      .on('mouseenter', function () {
        d3.select(this).select('rect').attr('stroke-width', 2).attr('stroke', '#374151');
      })
      .on('mousemove', function (event, d) {
        d3.select(this).select('rect').attr('stroke-width', 2).attr('stroke', '#374151');
        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 14,
          state: d.data.state,
          addressCount: d.data.addressCount,
          mtcAvailable: d.data.mtcAvailable,
          mtcCoverage: d.data.mtcCoverage,
          avgPest365: d.data.avgPest365,
        });
      })
      .on('mouseleave', function () {
        d3.select(this).select('rect').attr('stroke-width', 1).attr('stroke', '#ffffff');
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Re-render on container resize
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(() => {
      // Trigger re-render by re-dispatching the effect via a dummy state would be complex;
      // instead, directly re-invoke the D3 draw. We do this by clearing and re-running.
      if (svgRef.current && data && data.length > 0) {
        const event = new Event('resize');
        window.dispatchEvent(event);
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Service Coverage Map</div>
        <div style={styles.subtitle}>
          Treemap sized by address count, colored by mosquito/tick coverage
        </div>
        <div style={styles.emptyState}>No coverage data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Service Coverage Map</div>
      <div style={styles.subtitle}>
        Treemap sized by address count, colored by mosquito/tick coverage
      </div>
      <div ref={containerRef} style={{ width: '100%' }}>
        <svg ref={svgRef} />
      </div>

      {/* Color legend */}
      <div style={styles.legendContainer}>
        <div
          style={{
            ...styles.legendBar,
            background: 'linear-gradient(to right, #dc2626, #f97316, #fbbf24, #4ade80, #16a34a)',
          }}
        />
        <div style={styles.legendLabels}>
          <span>0% Coverage</span>
          <span>100% Coverage</span>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              color: '#1f2937',
              marginBottom: '6px',
              fontSize: '13px',
            }}
          >
            {tooltip.state}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '2px',
            }}
          >
            <span style={{ color: '#6b7280' }}>Addresses</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {tooltip.addressCount}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '2px',
            }}
          >
            <span style={{ color: '#6b7280' }}>MTC Available</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {tooltip.mtcAvailable} / {tooltip.addressCount}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '2px',
            }}
          >
            <span style={{ color: '#6b7280' }}>Coverage</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {tooltip.mtcCoverage.toFixed(1)}%
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
            }}
          >
            <span style={{ color: '#6b7280' }}>Avg Pest365</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              ${tooltip.avgPest365.toFixed(2)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
